// api/research.ts — the T1 RESEARCH DEPT human-review API, using the shared client
// (apiGet/apiPost). Paths are /api/research/admin/... (backend registers
// researchRoutes under /api + /research).
import { apiGet, apiPost } from "./client.ts";

export interface NicheRank {
  nicheId: string;
  vertical: string;
  subVertical: string | null;
  parentNicheId: string | null;
  candidates: number;
  kept: number;
  avgScore: number;
  affiliateShare: number;
  lastHarvest: string | null;
  nicheScore: number;
}

// ── Phase 4: arc view types (tf-043: ArcSend* → ResearchArc*) ──
export interface ResearchArcRollup {
  researchArcId: string;
  circleKey: string;
  total: number;
  pending: number;
  cloned: number;
  rendered: number;
  failed: number;
  cancelled: number;
  rendering: number;
  linkedAt: string;
}

export type ArcVideoStage =
  | "pending" | "tearing_down" | "torndown" | "cloned"
  | "rendering" | "rendered" | "failed" | "cancelled" | "awaiting_gate";

export interface ResearchArcVideoDetail {
  id: number;
  candidateId: string;
  circleKey: string;
  sequence: number;
  role: string | null;
  cloneId: string | null;
  arcStatus: string;
  arcError: string | null;
  teardownJobId: string | null;
  teardownStatus: string | null;
  teardownError: string | null;
  teardownReusedAt: string | null;
  renderRunId: string | null;
  renderStatus: string | null;
  gateWaiting: boolean;
  gateRowId: string | null;
  stage: ArcVideoStage;
  sourceUrl: string | null;
}

// A ranked casting candidate from cast search (semantic similarity + structured boost).
export interface CastCandidate {
  stockId: string;
  score: number;               // hybrid score; higher = closer match to the source persona
  gender: string | null;
  ageRange: string | null;
  capability: string | null;
  displayName: string | null;
  castingDesc: string | null;  // the character's casting description (what was embedded)
}

// The most recent cast recorded for an arc (if any).
export interface CurrentCast {
  id: string;
  characterStockId: string;
  castMode: string;
}

// A video's source appearance persona (teardown vision output) — what the AttachModal shows + casts against.
export interface SourcePersona {
  subjectType: string;
  subjectRef: string;
  platform: string;
  gender: string | null;
  ageRange: string | null;
  build: string | null;
  ethnicity: string | null;
  onCameraRole: string | null;
  description: string | null;
  brief: string | null;
  confidence: string | null;
}

export interface ResearchCreator {
  id: string;
  handle: string;
  platform: string;
  niche: string;
  subNiche: string | null;
  intent: "audience_growth" | "sales";
  followerCount: number | null;
  followerGrowthRate: number | null;
  avgShareRate: number | null;
  avgSaveRate: number | null;
  consistencyScore: number | null;
  creatorScore: number | null;
  status: "active" | "dropped";
}

export interface ResearchCandidate {
  id: string;
  sourceUrl: string;
  handle: string | null;
  platform: string;
  niche: string;
  intent: "audience_growth" | "sales";
  views: number | null;
  shares: number | null;
  saves: number | null;
  comments: number | null;
  shareRate: number | null;
  saveRate: number | null;
  videoScore: number | null;
  status: string;
  mp4Url: string | null;
}

const BASE = "/research/admin";

// ── management types ────────────────────────────────────────────────────────────
export interface ManagedNiche {
  id: string;
  vertical: string;
  subVertical: string | null;
  parentNicheId: string | null;
  intentFocus: string | null;
  warmth: string | null;
  status: "active" | "archived";
  keywordCount: number;
  candidateCount: number;
}

export interface ManagedKeyword {
  id: string;
  nicheId: string;
  keyword: string;
  intent: string | null;
  pattern: string | null;
  creatorsFound: number | null;
  avgFoundScore: number | null;
  keywordScore: number | null;
  status: "active" | "dropped";
}

function qs(params: Record<string, string | undefined>): string {
  const p = Object.entries(params).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v!)}`);
  return p.length ? `?${p.join("&")}` : "";
}

export interface CircleVideo {
  sourceUrl: string;
  creatorHandle: string;
  role: "hit" | "ramp";
  candidateId: string | null;   // null = hit not harvested as a candidate → not selectable
  shopCategoryId: string | null;
  spikeRatio: number | null;
  teardownJobId: string | null;   // P5: non-null → torn down, ready to make a video
}
export interface GridCell {
  cellKey: string;
  creatorSig: string;    // [physique,gender,age,persona]
  offeringSig: string;   // category[target_user]
  categoryLeaf: string | null;
  creatorCount: number;
  topCircleKey: string | null;
  topRankScore: number | null;
  avgConversion: number | null;
  tier: string;          // proven_sales | confirmed_organic | provisional_single_snap
}

export interface Circle {
  circleKey: string;
  channel: string;   // tiktok_shop | local_business | ecommerce
  platform: string;  // tiktok | youtube | instagram
  subNiche: string;
  personaLabel: string | null;
  arcLabel: string | null;
  memberCount: number;
  performance: number | null;
  velocity: number | null;
  rankScore: number | null;
  mature: boolean;
  videos: CircleVideo[];
}

export interface ShopCategory {
  categoryId: string; name: string; slug: string | null; level: number;
  isLeaf: boolean; parentCategoryId: string | null; mainCategoryId: string | null;
}

export const researchApi = {
  // 1a — niche finder view
  niches: async (): Promise<NicheRank[]> => {
    const r = await apiGet<{ niches?: NicheRank[] } | NicheRank[]>(`${BASE}/niches`);
    return Array.isArray(r) ? r : (r?.niches ?? []);
  },

  // TikTok Shop category tree — L1 (level 1, no parent) or L1's children (level 2 + parent).
  shopCategories: async (level: 1 | 2, parent?: string): Promise<ShopCategory[]> => {
    const q = new URLSearchParams({ level: String(level) });
    if (parent) q.set("parent", parent);
    const r = await apiGet<{ categories: ShopCategory[] }>(`${BASE}/shop/categories?${q}`);
    return r.categories;
  },

  // 1b — creator curation
  creators: async (filter: { niche?: string; intent?: string; status?: string } = {}): Promise<ResearchCreator[]> => {
    const r = await apiGet<{ creators?: ResearchCreator[] } | ResearchCreator[]>(`${BASE}/creators${qs(filter)}`);
    return Array.isArray(r) ? r : (r?.creators ?? []);
  },
  keepCreator: (id: string) => apiPost<{ ok: true; status: string }>(`${BASE}/creators/${id}/keep`, {}),
  dropCreator: (id: string) => apiPost<{ ok: true; status: string }>(`${BASE}/creators/${id}/drop`, {}),

  // 1c — candidate review
  candidates: async (filter: { niche?: string; intent?: string; status?: string } = {}): Promise<ResearchCandidate[]> => {
    const r = await apiGet<{ candidates?: ResearchCandidate[] } | ResearchCandidate[]>(`${BASE}/candidates${qs(filter)}`);
    return Array.isArray(r) ? r : (r?.candidates ?? []);
  },
  approveCandidate: (id: string) => apiPost<{ ok: true; status: string }>(`${BASE}/candidates/${id}/approve`, {}),
  rejectCandidate: (id: string) => apiPost<{ ok: true; status: string }>(`${BASE}/candidates/${id}/reject`, {}),

  // ── arcs / circles (P2) ──
  circles: async (filter: { channel?: string; limit?: number } = {}): Promise<Circle[]> => {
    const q = qs({ channel: filter.channel, limit: filter.limit != null ? String(filter.limit) : undefined });
    const r = await apiGet<{ circles?: Circle[] } | Circle[]>(`${BASE}/circles${q}`);
    return Array.isArray(r) ? r : (r?.circles ?? []);
  },

  grid: async (filter: { tier?: string; limit?: number } = {}): Promise<GridCell[]> => {
    const q = qs({ tier: filter.tier, limit: filter.limit != null ? String(filter.limit) : undefined });
    const r = await apiGet<{ cells?: GridCell[] } | GridCell[]>(`${BASE}/grid${q}`);
    return Array.isArray(r) ? r : (r?.cells ?? []);
  },
  // Phase 2c/4d: returns researchArcId so the UI can link straight to the arc view.
  sendArcsToTeardown: (candidateIds: string[]) =>
    apiPost<{ queued: number; researchArcId: string | null }>(`${BASE}/circles/teardown`, { candidateIds }),

  // ── Phase 4: arc-grouped view (the operator spine) — tf-043: /arcs → /research-arcs ──
  listResearchArcs: (limit = 50) =>
    apiGet<{ arcs: ResearchArcRollup[] }>(`${BASE}/research-arcs?limit=${limit}`).then((r) => r.arcs),
  researchArcDetail: (researchArcId: string) =>
    apiGet<{ videos: ResearchArcVideoDetail[] }>(`${BASE}/research-arcs/${encodeURIComponent(researchArcId)}`).then((r) => r.videos),
  cancelResearchArc: (researchArcId: string) =>
    apiPost<{ ok: boolean; cancelled: number }>(`${BASE}/research-arcs/${encodeURIComponent(researchArcId)}/cancel`, {}),
  cancelArcRow: (id: number) =>
    apiPost<{ ok: boolean }>(`${BASE}/research-arcs/rows/${id}/cancel`, {}),

  // (TF3-1, 10-14) cloneVideoFromArc (a clone board, seeded with the source frame as the product image, T9-19) and
  //  makeVideoFromArc (a backend remix, T9-3) are deleted with the routes they called.

  // ── casting (semantic cast search + the cast action) ──
  // castSearch: free-text (a source-persona description) → ranked character candidates with scores.
  castSearch: async (params: {
    text: string; gender?: string | null; capability?: "talking_head" | "motion" | null;
    platform?: string | null; ageRange?: string | null; limit?: number;
  }): Promise<CastCandidate[]> => {
    const q = qs({
      text: params.text, gender: params.gender ?? undefined, capability: params.capability ?? undefined,
      platform: params.platform ?? undefined, ageRange: params.ageRange ?? undefined,
      limit: params.limit != null ? String(params.limit) : undefined,
    });
    const r = await apiGet<{ candidates?: CastCandidate[] }>(`${BASE}/cast/search${q}`);
    return r?.candidates ?? [];
  },
  // castForArc: given an arc (circleKey) + optionally its winning video, return the current cast (if
  // any), the video's source persona, plus auto-generated candidates ranked by match to that persona.
  castForArc: async (circleKey: string, opts: { videoUrl?: string; platform?: string } = {}): Promise<{
    current: CurrentCast | null; candidates: CastCandidate[]; candidatesError?: string | null; persona: SourcePersona | null;
  }> => {
    const q = qs({ videoUrl: opts.videoUrl, platform: opts.platform });
    return apiGet<{ current: CurrentCast | null; candidates: CastCandidate[]; candidatesError?: string | null; persona: SourcePersona | null }>(
      `${BASE}/cast/for-arc/${encodeURIComponent(circleKey)}${q}`,
    );
  },
  // getAppearance: fetch just a video's source persona (no casting) — for showing the persona chip on
  // a teardown row. Returns null if none yet (sink off / not torn down) → caller degrades to plain list.
  getAppearance: async (videoUrl: string, platform: string): Promise<SourcePersona | null> => {
    const q = qs({ videoUrl, platform });
    const r = await apiGet<{ persona: SourcePersona | null }>(`${BASE}/appearance${q}`);
    return r?.persona ?? null;
  },
  // recordCast: commit a casting decision (writes arc_castings + the vms_castings judgment index).
  recordCast: (body: {
    circleKey: string; characterStockId: string;
    castMode?: "manual" | "auto" | "auto_accepted" | "auto_overridden";
    castScore?: number | null; sourcePersona?: Record<string, unknown> | null;
  }) => apiPost<{ ok: boolean; castingId: string }>(`${BASE}/cast`, body),

  // ── niche management ──
  manageNiches: async (): Promise<ManagedNiche[]> => {
    const r = await apiGet<{ niches?: ManagedNiche[] } | ManagedNiche[]>(`${BASE}/niches/manage`);
    return Array.isArray(r) ? r : (r?.niches ?? []);
  },
  createNiche: (body: { vertical: string; subVertical?: string | null; parentVertical?: string | null; products?: string[]; problems?: string[] }) =>
    apiPost<{ nicheId: string; keywordsGenerated: number; keywordsAdded: number; created: boolean }>(`${BASE}/niches`, body),
  archiveNiche: (id: string) => apiPost<{ ok: true; status: string }>(`${BASE}/niches/${id}/archive`, {}),
  activateNiche: (id: string) => apiPost<{ ok: true; status: string }>(`${BASE}/niches/${id}/activate`, {}),

  // ── keyword management ──
  keywords: async (nicheId: string, status?: string): Promise<ManagedKeyword[]> => {
    const q = status ? `?status=${encodeURIComponent(status)}` : "";
    const r = await apiGet<{ keywords?: ManagedKeyword[] } | ManagedKeyword[]>(`${BASE}/niches/${nicheId}/keywords${q}`);
    return Array.isArray(r) ? r : (r?.keywords ?? []);
  },
  addKeyword: (nicheId: string, keyword: string, intent?: "audience_growth" | "sales" | null) =>
    apiPost<{ id: string; added: boolean }>(`${BASE}/niches/${nicheId}/keywords`, { keyword, intent }),
  dropKeyword: (id: string) => apiPost<{ ok: true; status: string }>(`${BASE}/keywords/${id}/drop`, {}),
  activateKeyword: (id: string) => apiPost<{ ok: true; status: string }>(`${BASE}/keywords/${id}/activate`, {}),
};
